import {I18n} from '@aws-amplify/core';
import {useParams} from 'react-router-dom';
import {Alert, Code} from '@mantine/core';

import {
    formatTimestamp,
    FormPageLayout,
    PlatformEditPageGrid,
    PlatformLoader,
} from '@flexinet/ui-components';

import {Profile} from '../profile/Profile.tsx';
import {FindingsPanel} from '../profile/FindingsPanel.tsx';
import {EditPageNavigation} from '../navigation/EditPageNavigation.tsx';
import {useBindingDriftReport} from '../hooks/useBindingDriftReport.ts';
import {moduleConfig} from '../config';

// FNP-585: read-only detail view of a single binding-drift sweep run. No edit drawer
// / tools — the sweep owns the lifecycle. The run summary and outcome counts sit in
// the left panel, the per-account findings in the right.
export const DetailPage = () => {
    const {id: reportId} = useParams();

    if (typeof reportId === 'undefined') {
        throw new Error('reportId is required parameter');
    }

    const {
        record: report,
        loading,
        isError,
        notFound,
    } = useBindingDriftReport(reportId);

    if (loading) {
        return <PlatformLoader message={I18n.get('Loading binding drift report...Please wait')}/>;
    }

    const singular = I18n.get(moduleConfig.titles.singular);

    // Never hang on the loader: once the query has settled, a failed load and a
    // missing record each get their own message, inside the page frame so the
    // breadcrumbs still lead back to the list.
    if (isError || notFound || !report) {
        return (
            <FormPageLayout title={singular} pageNavigation={<EditPageNavigation id={reportId}/>}>
                {isError
                    ? (
                        <Alert color='red' title={I18n.get('Could not load the binding drift report')}>
                            {I18n.get('The billing API returned an error for')} <Code>{reportId}</Code>.{' '}
                            {I18n.get('Reload the page; if it persists, the API may be unavailable in this environment.')}
                        </Alert>
                    )
                    : (
                        <Alert color='orange' title={I18n.get('Binding drift report not found')}>
                            {I18n.get('No binding drift report exists with id')} <Code>{reportId}</Code>.
                        </Alert>
                    )}
            </FormPageLayout>
        );
    }

    // FormPageLayout's title is a STRING. The app's utils/TableTimestamps formatter
    // returns a JSX fragment, and a fragment inside a template literal prints
    // "[object Object]" — which tsc accepts. The library formatter returns a string
    // (the same one the Run Started At card uses); the guard keeps a non-string from
    // ever reaching the heading again.
    const started = formatTimestamp(report.runStartedAt);
    const title = `${singular}: ${typeof started === 'string' ? started : report.runStartedAt}`;

    return (
        <FormPageLayout
            title={title}
            pageNavigation={<EditPageNavigation id={report.id}/>}
        >
            <PlatformEditPageGrid
                leftContent={<Profile record={report}/>}
                rightContent={<FindingsPanel record={report}/>}
            />
        </FormPageLayout>
    );
};
